import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useFindMany, useAction, useGlobalAction } from "@gadgetinc/react";
import { api } from "../../../api";
import { safeArrayMap } from "./configHelpers";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Input,
  Select,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
  SimpleGrid,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  IconButton,
  Collapse,
  Heading,
  Flex,
} from "@chakra-ui/react";
import { EditIcon, ChevronDownIcon, ChevronUpIcon, AddIcon, DeleteIcon } from "@chakra-ui/icons";

export default function MemberManagementModal({ isOpen, onClose, cohort, onMemberUpdate }) {
  const [{ data: memberships, fetching: loadingMemberships, error: membershipsError }, refetchMemberships] = useFindMany(api.cohortMembership, {
    filter: {
      cohort: { id: { equals: cohort?.id } },
      active: { equals: true },
      userId: { isSet: true }
    },
    select: {
      id: true,
      primaryRole: true,
      subRoles: true,
      notificationPreferences: true,
      hasFisdapReviewAccess: true,
      hasScenarioGeneratorAccess: true,
      assignedAt: true,
      user: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        individualScenarioAccess: true,
        agentAccessMode: true,
        accessibleAgentNames: true,
        assistantAccessMode: true,
        accessibleAssistantIds: true,
        scenarioCreditSources: true,
      }
    },
    pause: !isOpen || !cohort?.id
  });

  const [{ fetching: updatingMembership }, updateCohortMembership] = useAction(api.cohortMembership.update);
  const [{ fetching: updatingScenarioAccess }, updateUserScenarioAccess] = useGlobalAction(api.updateUserScenarioAccess);

  const [{ data: availableUsers, fetching: loadingUsers }, refetchUsers] = useFindMany(api.user, {
    filter: { institution: { equals: cohort?.institution } },
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      individualScenarioAccess: true,
      agentAccessMode: true,
      accessibleAgentNames: true,
      assistantAccessMode: true,
      accessibleAssistantIds: true,
      scenarioCreditSources: true,
    },
    pause: !isOpen || !cohort?.institution
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setLastRefreshTime(new Date());
      refetchMemberships();
      refetchUsers();
    }
  }, [isOpen, refetchMemberships, refetchUsers]);

  const getUserRole = useCallback((user) => {
    if (!user || !memberships) return "Not a member";
    const membership = memberships.find(m => m.user.id === user.id);
    if (!membership) return "Not a member";
    if (membership.subRoles && membership.subRoles.length > 0) {
      return `${membership.primaryRole} (${membership.subRoles.join(', ')})`;
    }
    return membership.primaryRole;
  }, [memberships]);

  const currentMembers = useMemo(() => {
    if (!memberships || !Array.isArray(memberships)) return [];
    return memberships.map(m => m.user).filter(Boolean);
  }, [memberships, refreshKey]);

  const instructors = useMemo(() => {
    if (!Array.isArray(memberships)) return [];
    return memberships.filter(m => m.primaryRole === 'instructor').map(m => m.user).filter(Boolean);
  }, [memberships, refreshKey]);

  const staff = useMemo(() => {
    if (!Array.isArray(memberships)) return [];
    return memberships.filter(m => m.primaryRole === 'staff').map(m => m.user).filter(Boolean);
  }, [memberships, refreshKey]);

  const students = useMemo(() => {
    if (!Array.isArray(memberships)) return [];
    return memberships.filter(m => m.primaryRole === 'student').map(m => m.user).filter(Boolean);
  }, [memberships, refreshKey]);

  const availableToAdd = useMemo(() => {
    if (!Array.isArray(availableUsers) || !Array.isArray(currentMembers)) return [];
    const currentMemberIds = new Set(currentMembers.map(m => m.id));
    return availableUsers.filter(user =>
      user &&
      !currentMemberIds.has(user.id) &&
      (searchTerm === "" ||
       user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       user.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [availableUsers, currentMembers, searchTerm, refreshKey]);

  const handleAddMembers = async () => {
    if (membersToAdd.length === 0) return;
    try {
      for (const userId of membersToAdd) {
        await api.cohortMembership.create({
          user: { _link: userId },
          cohort: { _link: cohort.id },
          primaryRole: 'student',
          subRoles: [],
          active: true,
          assignedAt: new Date()
        });
      }
      toast({ title: "Members Added", description: `Successfully added ${membersToAdd.length} member(s) to the cohort.`, status: "success", duration: 3000, isClosable: true });
      setMembersToAdd([]);
      setSearchTerm("");
      refetchMemberships();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
    } catch (error) {
      toast({ title: "Error Adding Members", description: error.message || "Failed to add members to cohort", status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      const membership = memberships?.find(m => m.user.id === userId);
      if (!membership) return;
      await api.cohortMembership.delete(membership.id);
      refetchMemberships();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
      setRefreshKey(prev => prev + 1);
      toast({ title: "Member Removed", description: "Successfully removed member from the cohort.", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Error Removing Member", description: error.message || "Failed to remove member from cohort", status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleRoleChange = async (userId, newRole, specializations = []) => {
    try {
      const membership = memberships?.find(m => m.user.id === userId);
      if (!membership) return;
      await api.cohortMembership.update(membership.id, {
        primaryRole: newRole.toLowerCase(),
        subRoles: specializations
      });
      refetchMemberships();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
      toast({ title: "Role Updated", description: "Successfully updated member role.", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Error Updating Role", description: error.message || "Failed to update member role", status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleUpdateNotificationPreferences = async (membershipId, preferences) => {
    try {
      await updateCohortMembership({ id: membershipId, notificationPreferences: preferences });
      toast({ title: 'Preferences Updated', description: 'Notification preferences have been saved', status: 'success', duration: 3000, isClosable: true });
      refetchMemberships();
      if (onMemberUpdate) await onMemberUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update preferences', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleUpdateFisdapAccess = async (membershipId, hasAccess) => {
    try {
      await updateCohortMembership({ id: membershipId, hasFisdapReviewAccess: hasAccess });
      toast({ title: 'FISDAP Access Updated', description: hasAccess ? 'FISDAP review access granted' : 'FISDAP review access revoked', status: 'success', duration: 3000, isClosable: true });
      refetchMemberships();
      if (onMemberUpdate) await onMemberUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update FISDAP access', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleUpdateScenarioAccess = async (membershipId, hasAccess) => {
    try {
      const accessValue = Boolean(hasAccess);
      const membership = memberships?.find(m => m.id === membershipId);
      if (!membership) return;
      const result = await updateUserScenarioAccess({ userId: membership.user.id, hasAccess: accessValue });
      if (result.success) {
        let description = result.cohortMembershipsUpdated > 0
          ? `Updated ${result.cohortMembershipsUpdated} cohort membership(s) and individual access`
          : 'Updated individual scenario access (user has no active cohort memberships)';
        toast({ title: 'Scenario Generator Access Updated', description, status: 'success', duration: 3000, isClosable: true });
      } else {
        toast({ title: 'Error Updating Access', description: result.error || 'Failed to update access', status: 'error', duration: 5000, isClosable: true });
      }
      refetchMemberships();
      if (onMemberUpdate) await onMemberUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update scenario generator access', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const toggleNotificationSettings = (userId) => {
    const newExpanded = new Set(expandedNotifications);
    if (newExpanded.has(userId)) newExpanded.delete(userId);
    else newExpanded.add(userId);
    setExpandedNotifications(newExpanded);
  };

  // Render a member row for a given role section
  const renderMemberRow = (member, roleValue, bgColor) => {
    const membership = memberships.find(m => m.user.id === member.id);
    const specializations = membership?.subRoles || [];
    const notificationPrefs = membership?.notificationPreferences || {
      notifyOnPass: false, notifyOnFail: true, notifyOnNeedsReview: true, notifyOnDocSubmission: false, emailFrequency: 'immediate'
    };
    const isNotificationExpanded = expandedNotifications.has(member.id);
    const hasFisdapAccess = membership?.hasFisdapReviewAccess === true;
    const hasScenarioAccess = (membership?.hasScenarioGeneratorAccess ?? false) === true || member.individualScenarioAccess === true;

    const roleOptions = ['Instructor', 'Staff', 'Student'].filter(r => r !== roleValue);

    return (
      <Box key={`${roleValue.toLowerCase()}-${member.id}`}>
        <Flex p={3} bg={bgColor} borderRadius="md" align="center">
          <VStack align="start" spacing={0} flex="1">
            <Text fontWeight="bold">{member.firstName} {member.lastName}</Text>
            <Text fontSize="sm" color="gray.300">{member.email}</Text>
            <HStack spacing={1} mt={1} flexWrap="wrap">
              {roleValue === 'Instructor' && (
                specializations.length > 0 ? specializations.map(spec => (
                  <Badge key={spec} size="xs" colorScheme="blue">{spec}</Badge>
                )) : (
                  <Badge size="xs" colorScheme="gray">No specializations</Badge>
                )
              )}
              {roleValue === 'Staff' && (
                specializations.length > 0 ? specializations.map(role => (
                  <Badge key={role} size="xs" colorScheme="green">{role}</Badge>
                )) : (
                  <Badge size="xs" colorScheme="gray">No sub-roles</Badge>
                )
              )}
              {roleValue === 'Student' && (
                <Text fontSize="xs" color="gray.400">{getUserRole(member)}</Text>
              )}
              {hasFisdapAccess && <Badge size="xs" colorScheme="green">FISDAP Review</Badge>}
              {(membership?.hasScenarioGeneratorAccess === true || member.individualScenarioAccess === true) && (
                <HStack spacing={1}>
                  {membership?.hasScenarioGeneratorAccess === true && <Badge size="xs" colorScheme="purple">Cohort Scenario Access</Badge>}
                  {member.individualScenarioAccess === true && <Badge size="xs" colorScheme="green">Individual Scenario Access</Badge>}
                  {roleValue === 'Instructor' && cohort?.grantScenarioAccessToInstructors === true && (
                    <Badge size="xs" colorScheme="purple" variant="outline">Auto-granted (Cohort Setting)</Badge>
                  )}
                </HStack>
              )}
            </HStack>
            {member.scenarioCreditSources && member.scenarioCreditSources.length > 0 && (
              <Text fontSize="xs" color="gray.400">Credits: {member.scenarioCreditSources.join(', ')}</Text>
            )}
          </VStack>
          <VStack align="start" spacing={2}>
            <Select
              value={roleValue}
              onChange={(e) => {
                if (e.target.value !== roleValue) {
                  handleRoleChange(member.id, e.target.value, e.target.value === roleValue ? specializations : []);
                }
              }}
              size="sm" bg="gray.600" borderColor="gray.500" minW="120px"
            >
              <option value={roleValue}>{roleValue}</option>
              {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </Select>
            {roleValue === 'Instructor' && (
              <VStack align="start" spacing={1} pl={2}>
                <Text fontSize="xs" color="gray.400">Specializations:</Text>
                {['didactic', 'lab', 'clinical', 'field'].map(spec => (
                  <Checkbox key={spec} size="sm"
                    isChecked={specializations.includes(spec)}
                    onChange={(e) => {
                      const newSpecs = e.target.checked
                        ? [...specializations, spec]
                        : specializations.filter(s => s !== spec);
                      handleRoleChange(member.id, 'Instructor', newSpecs);
                    }}
                  >
                    <Text fontSize="xs">{spec.charAt(0).toUpperCase() + spec.slice(1)}</Text>
                  </Checkbox>
                ))}
              </VStack>
            )}
            <VStack align="start" spacing={1} pl={2} mt={roleValue === 'Instructor' ? 2 : 0}>
              {roleValue === 'Instructor' && <Text fontSize="xs" color="gray.400">Access:</Text>}
              <Checkbox size="sm" isChecked={hasFisdapAccess}
                onChange={(e) => handleUpdateFisdapAccess(membership.id, e.target.checked)} colorScheme="green">
                <Text fontSize="xs">FISDAP Review{roleValue !== 'Instructor' ? '' : ' Access'}</Text>
              </Checkbox>
              <Checkbox size="sm" isChecked={hasScenarioAccess}
                onChange={(e) => handleUpdateScenarioAccess(membership.id, e.target.checked)} colorScheme="purple">
                <Text fontSize="xs">Scenario Generator Access</Text>
              </Checkbox>
            </VStack>
            <HStack spacing={2}>
              {roleValue === 'Instructor' && (
                <Button size="xs"
                  leftIcon={isNotificationExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  onClick={() => toggleNotificationSettings(member.id)}
                  colorScheme="teal" variant="outline">
                  Notifications
                </Button>
              )}
              <IconButton icon={<DeleteIcon />} onClick={() => handleRemoveMember(member.id)}
                colorScheme="red" variant="ghost" size="sm" />
            </HStack>
          </VStack>
        </Flex>

        {/* Notification settings collapse (instructors only) */}
        {roleValue === 'Instructor' && (
          <Collapse in={isNotificationExpanded}>
            <Box p={4} bg="blue.800" borderRadius="md" mt={2} ml={4}>
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="bold" color="white">
                  Notification Settings for {member.firstName} {member.lastName}
                </Text>
                <VStack spacing={2} align="stretch">
                  {[
                    { key: 'notifyOnPass', label: 'Notify on student shift pass', scheme: 'green' },
                    { key: 'notifyOnFail', label: 'Notify on student shift fail', scheme: 'red' },
                    { key: 'notifyOnNeedsReview', label: 'Notify on student shift needs review', scheme: 'yellow' },
                    { key: 'notifyOnDocSubmission', label: 'Notify on clinical document submission', scheme: 'blue' },
                  ].map(({ key, label, scheme }) => (
                    <Checkbox key={key}
                      isChecked={notificationPrefs[key] || false}
                      onChange={(e) => handleUpdateNotificationPreferences(membership.id, { ...notificationPrefs, [key]: e.target.checked })}
                      size="sm" colorScheme={scheme}>
                      <Text fontSize="sm">{label}</Text>
                    </Checkbox>
                  ))}
                </VStack>
                <FormControl>
                  <FormLabel fontSize="sm" color="white">Email Notification Frequency</FormLabel>
                  <Select
                    value={notificationPrefs.emailFrequency || 'immediate'}
                    onChange={(e) => handleUpdateNotificationPreferences(membership.id, { ...notificationPrefs, emailFrequency: e.target.value })}
                    size="sm" bg="gray.600" borderColor="gray.500" color="white">
                    <option value="immediate">Immediate</option>
                    <option value="daily">Daily Digest</option>
                    <option value="weekly">Weekly Digest</option>
                    <option value="none">No Emails</option>
                  </Select>
                </FormControl>
                {updatingMembership && (
                  <HStack spacing={2}>
                    <Spinner size="xs" color="blue.400" />
                    <Text fontSize="xs" color="gray.300">Saving preferences...</Text>
                  </HStack>
                )}
              </VStack>
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Manage Cohort Members - {cohort?.displayName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="md">Current Members ({currentMembers.length})</Heading>
                <HStack spacing={2}>
                  <Button size="sm" colorScheme="gray" variant="outline"
                    onClick={() => { setLastRefreshTime(new Date()); refetchUsers(); }}
                    leftIcon={<EditIcon />}>
                    Refresh Data
                  </Button>
                  <Button size="sm" colorScheme="yellow" variant="outline"
                    onClick={() => setShowDebugPanel(!showDebugPanel)}>
                    {showDebugPanel ? 'Hide Debug' : 'Show Debug'}
                  </Button>
                </HStack>
              </HStack>

              {showDebugPanel && (
                <Box bg="gray.900" p={4} borderRadius="md" mb={4} fontSize="xs">
                  <VStack spacing={2} align="stretch">
                    <Text color="yellow.300" fontWeight="bold">Debug Panel</Text>
                    <Text color="gray.300">Last Refresh: {lastRefreshTime.toLocaleTimeString()}</Text>
                    <Text color="gray.300">Loading Users: {loadingUsers ? 'TRUE' : 'FALSE'}</Text>
                    <Text color="gray.300">Available Users Count: {availableUsers?.length || 0}</Text>
                    <Text color="gray.300">Current Members Count: {currentMembers?.length || 0}</Text>
                    <Text color="gray.300">Cohort ID: {cohort?.id}</Text>
                    <Text color="gray.300">Institution: {cohort?.institution}</Text>
                  </VStack>
                </Box>
              )}

              {loadingUsers ? (
                <VStack spacing={4} align="center" py={8}>
                  <Spinner size="lg" color="blue.400" />
                  <Text color="gray.300">Loading member data and agent access information...</Text>
                  <Text fontSize="sm" color="gray.400">This may take a few seconds</Text>
                </VStack>
              ) : currentMembers.length === 0 ? (
                <Alert status="info"><AlertIcon /><Text>No members in this cohort yet.</Text></Alert>
              ) : (
                <VStack spacing={4} align="stretch" maxH="400px" overflowY="auto">
                  {instructors.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="blue.300" mb={2}>
                        Instructors ({instructors.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(instructors, member => renderMemberRow(member, 'Instructor', 'blue.900'))}
                      </VStack>
                    </Box>
                  )}
                  {staff.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="green.300" mb={2}>
                        Staff ({staff.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(staff, member => renderMemberRow(member, 'Staff', 'green.900'))}
                      </VStack>
                    </Box>
                  )}
                  {students.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.300" mb={2}>
                        Students ({students.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(students, member => renderMemberRow(member, 'Student', 'gray.700'))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>

            <Divider />

            {/* Add Members Section */}
            <Box>
              <Heading size="md" mb={4}>Add New Members</Heading>
              <VStack spacing={4} align="stretch">
                <Input placeholder="Search users by name or email..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} bg="gray.700" borderColor="gray.600" />
                {searchTerm && (
                  <Box>
                    <Text fontSize="sm" color="gray.300" mb={2}>
                      Available Users ({Array.isArray(availableToAdd) ? availableToAdd.length : 0})
                    </Text>
                    <VStack spacing={2} align="stretch" maxH="200px" overflowY="auto">
                      {safeArrayMap(availableToAdd, user => (
                        <HStack key={user.id} p={2} bg="gray.700" borderRadius="md">
                          <Checkbox isChecked={membersToAdd.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) setMembersToAdd(prev => [...prev, user.id]);
                              else setMembersToAdd(prev => prev.filter(id => id !== user.id));
                            }} />
                          <VStack align="start" spacing={0} flex="1">
                            <Text fontWeight="bold">{user.firstName} {user.lastName}</Text>
                            <Text fontSize="sm" color="gray.300">{user.email}</Text>
                          </VStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}
                {membersToAdd.length > 0 && (
                  <HStack>
                    <Button onClick={handleAddMembers} colorScheme="green" size="sm" leftIcon={<AddIcon />}>
                      Add {membersToAdd.length} Selected Member(s)
                    </Button>
                    <Button onClick={() => setMembersToAdd([])} variant="ghost" size="sm">Clear Selection</Button>
                  </HStack>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
